import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { EducationalProviderDetail } from '../../models/educational-provider-detail.interface';
import { EducationalProvider } from '../../models/educational-provider/educational-provider.interface';
import { AccreditingBody } from '../../models/application/accrediting-body.interface';
import { EducationalProviderSearchResult } from '../../models/educational-provider/educational-provider-search-result.interface';
import { EducationalProviderSearchQuery } from '../../models/educational-provider/educational-provider-search-query.interface';
import { HttpParamObject } from '../../utility/http-utility/http-param-object.interface';
import { HttpUtilityService } from '../../utility/http-utility/http-utility.service';
import { AccreditationSearchQuery } from '../../models/educational-provider/accreditation-search-query.interface';
import { ObjectUtilityService } from '@edassist/common';
import {
	EducationalProviderAdminSearch,
	EducationalProviderCode,
	ProgramProvider,
	ProgramProvidersSearch,
	ProviderAccreditation,
	ProviderClient,
	ProviderCourseOfStudy,
	ProviderSubscription
} from '@edassist/api';
import { ProviderCourseOfStudyResponse } from '../../models/provider/provider-course-of-study.interface';

@Injectable({ providedIn: 'root' })
export class EducationProviderService {
	$educationProviderData: Observable<EducationalProvider>;
	private readonly educationProviderSubject: ReplaySubject<EducationalProvider>;
	private readonly relativeUrl = 'educationProviders/';
	private readonly providerUrl = 'education-providers';
	private readonly programsUrl = 'programs/';
	private readonly applicationRelativeUrl = 'applications/';

	private selectedProviderSubject = new BehaviorSubject<EducationalProvider | null>(null);
	selectedProvider$ = this.selectedProviderSubject.asObservable();

	constructor(private readonly http: HttpClient) {
		this.educationProviderSubject = new ReplaySubject<EducationalProvider>();
		this.$educationProviderData = this.educationProviderSubject.asObservable();
	}

	selectProvider(provider: EducationalProvider): void {
		if (this.selectedProviderSubject.getValue() !== provider) {
			this.selectedProviderSubject.next(provider);
		}
	}

	saveEducationProvider(
		applicationId: number,
		educationProvider: EducationalProviderDetail
	): Observable<EducationalProviderDetail> {
		return this.http.post<EducationalProviderDetail>(
			`${this.relativeUrl}${applicationId.toString()}`,
			educationProvider
		);
	}

	getEducationProvider(applicationId: number): void {
		this.http
			.get<Array<EducationalProvider>>(`${this.applicationRelativeUrl}${applicationId.toString()}/education-providers`)
			.subscribe((educationProvider: Array<EducationalProvider>) => {
				this.educationProviderSubject.next(educationProvider[0]);
			});
	}

    getIneligibleEducationProviders(programId: number, providerId: number): Observable<any> {
        return this.http.get<any>(
            `${this.programsUrl}${programId}/ineligible-providers/${providerId}`
        );
    }


	updateEducationProviderOnApplication(
		applicationId: number,
		educationProvider: EducationalProvider
	): Observable<EducationalProviderDetail> {
		return this.http.put<EducationalProviderDetail>(
			`${this.applicationRelativeUrl}${applicationId.toString()}/` + `${this.providerUrl}/${educationProvider.id}`,
			educationProvider
		);
	}

	searchAccreditations(accreditationSearchQuery: AccreditationSearchQuery): Observable<Array<AccreditingBody>> {
		const params = ObjectUtilityService.replaceNullOrUndefined(accreditationSearchQuery);
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(params);

		return this.http.get<Array<AccreditingBody>>('accreditations', queryParams);
	}

	searchEducationProvider(
		educationProviderSearchQuery: EducationalProviderSearchQuery
	): Observable<EducationalProviderSearchResult> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(educationProviderSearchQuery);

		return this.http.get<EducationalProviderSearchResult>(`${this.providerUrl}/search`, queryParams);
	}

	adminSearchEducationProviders(searchParams: EducationalProviderAdminSearch): Observable<Array<EducationalProvider>> {
		return this.http.post<Array<EducationalProvider>>('education-providers/admin-search', searchParams);
	}

	getEducationProviderById(providerId: number): Observable<EducationalProvider> {
		return this.http.get<EducationalProvider>(`education-providers/${providerId}`);
	}

	getEducationProviderByCode(providerCode: string): Observable<EducationalProvider> {
		return this.http.get<EducationalProvider>(`education-providers/provider-code/${providerCode}`);
	}

	getProgramProvidersByProviderId(providerId: number): Observable<Array<ProgramProvider>> {
		return this.http.get<Array<ProgramProvider>>(`education-providers/${providerId}/program-providers`);
	}

	getProviderAccreditationsByProviderId(providerId: number): Observable<Array<ProviderAccreditation>> {
		return this.http.get<Array<ProviderAccreditation>>(`education-providers/${providerId}/provider-accreditations`);
	}

	patchProviderAccreditations(providerId: number, patchObjs: Array<any>): Observable<ProviderAccreditation> {
		return this.http.patch<ProviderAccreditation>(
			`education-providers/provider-accreditations/${providerId}`,
			patchObjs
		);
	}

	postProviderAccreditations(providerAccreditation: ProviderAccreditation): Observable<ProviderAccreditation> {
		return this.http.post<ProviderAccreditation>(`education-providers/provider-accreditations`, providerAccreditation);
	}

	patchEducationProvider(providerId: number, patchObjs: Array<any>): Observable<EducationalProvider> {
		return this.http.patch<EducationalProvider>(`education-providers/${providerId}`, patchObjs);
	}

	getProviderCoursesOfStudy(providerId: number, index: number, recordsPerPage: number): Observable<ProviderCourseOfStudyResponse> {
		return this.http.get<ProviderCourseOfStudyResponse>(
			`educational-providers/${providerId}/courses-of-study?index=${index}&recordsPerPage=${recordsPerPage}`
		);
	}

	getSelectedCourseOfStudy(providerId: number): Observable<Array<ProviderCourseOfStudy>> {
		return this.http.get<Array<ProviderCourseOfStudy>>(
			`educational-providers/${providerId}/courses-of-study/all`
		);
	}

	getEducationalProviderClients(providerId: number): Observable<Array<ProviderClient>> {
		return this.http.get<Array<ProviderClient>>(`educational-providers/${providerId}/clients`);
	}

	addEdProviderClients(providerId: number): Observable<Array<ProviderClient>> {
		return this.http.post<Array<ProviderClient>>(`educational-providers/${providerId}/clients`, {});
	}

	updateEdProviderClients(providerId: number, patchObj: Array<any>): Observable<Array<ProviderClient>> {
		return this.http.put<Array<ProviderClient>>(`educational-providers/${providerId}/clients`, patchObj);
	}

	getEducationalProviderSubscription(subscriptionId: number): Observable<ProviderSubscription> {
		return this.http.get<ProviderSubscription>(`educational-provider/subscription/${subscriptionId}`);
	}

	updateProviderCoursesOfStudy(providerId: number, educationProviders: Array<ProviderCourseOfStudy>): Observable<Array<ProviderCourseOfStudy>> {

		return this.http.put<Array<ProviderCourseOfStudy>>(
			`educational-providers/${providerId}/courses-of-study`,
			educationProviders
		);
	}

	addProviderCoursesOfStudy(providerId: number): Observable<Array<ProviderCourseOfStudy>> {

		return this.http.post<Array<ProviderCourseOfStudy>>(
			`educational-providers/${providerId}/courses-of-study`,
			{}
		);
	}

	addProgramProvider(programProvider: ProgramProvider): Observable<ProgramProvider> {
		return this.http.post<ProgramProvider>('education-providers/program-providers', programProvider);
	}

	patchProgramProvider(programProviderId: number, patchObjs: Array<any>): Observable<ProgramProvider> {
		return this.http.patch<ProgramProvider>(`education-providers/program-providers/${programProviderId}`, patchObjs);
	}

	getEducationalProvidersByParentCode(parentCode: string): Observable<Array<EducationalProvider>> {
		return this.http.get<Array<EducationalProvider>>(`education-providers/parent-code/${parentCode}`);
	}

	getNewProviderCode(): Observable<EducationalProviderCode> {
		return this.http.get<EducationalProviderCode>(`educational-providers/new-provider-code`);
	}

	createEducationalProvider(educationProvider: EducationalProvider): Observable<EducationalProvider> {
		return this.http.post<EducationalProvider>(`educational-providers`, educationProvider);
	}

	programProvidersSearch(programProvidersSearch: ProgramProvidersSearch): Observable<Array<ProgramProvider>> {
		return this.http.post<Array<ProgramProvider>>(
			'education-providers/program-providers/search',
			programProvidersSearch
		);
	}
}
